/**
 * PublicProfile - Public profile page at /@{tag}
 * Shows user's display name, avatar, and Send Money CTA
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  AtSign, 
  Send, 
  Loader2, 
  AlertCircle, 
  ArrowLeft,
  Share2,
  Copy,
  QrCode
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import api from '../lib/api';

const PublicProfile = () => {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const cleanTag = tag?.replace('@', '');
        const { data } = await api.get(`/v1/tags/${cleanTag}`);
        setProfile(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('User not found');
        } else {
          setError('Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    if (tag) {
      fetchProfile();
    }
  }, [tag]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(profile?.profile_url || window.location.href);
    toast.success('Link copied!');
  };

  const shareProfile = async () => {
    if (navigator.share && profile) {
      try {
        await navigator.share({
          title: `Send money to ${profile.display_name}`,
          text: `Pay ${profile.tag_display} on ParachPay`,
          url: profile.profile_url
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const handleSendMoney = () => {
    // Navigate to transfer page with tag pre-filled
    navigate(`/transfer?to=${profile?.tag_display}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#022c22] to-[#064e3b] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#022c22] to-[#064e3b] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Profile Not Found</h1>
            <p className="text-emerald-100/70 mb-6">
              The tag @{tag?.replace('@', '')} doesn't exist or may have been released.
            </p>
            <div className="flex flex-col gap-3">
              <Button asChild className="w-full bg-emerald-500 hover:bg-emerald-600">
                <Link to="/register">Create Your Own Tag</Link>
              </Button>
              <Button variant="ghost" asChild className="text-emerald-100">
                <Link to="/">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#022c22] to-[#064e3b] flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        {/* Main Profile Card */}
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden" data-testid="public-profile-card">
          {/* Header Banner */}
          <div className="h-24 bg-gradient-to-r from-emerald-600 to-emerald-400" />
          
          <CardContent className="p-6 -mt-12">
            {/* Avatar */}
            <div className="flex justify-center mb-4">
              <Avatar className="h-24 w-24 border-4 border-[#022c22] bg-emerald-600">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.display_name} className="object-cover" />
                ) : (
                  <AvatarFallback className="bg-emerald-600 text-white text-2xl font-bold">
                    {getInitials(profile?.display_name)}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>

            {/* Name & Tag */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-white mb-1">
                {profile?.display_name}
              </h1>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-lg font-mono">
                <AtSign className="h-4 w-4 mr-1" />
                {profile?.tag}
              </Badge>
            </div>

            {/* QR Code Toggle */}
            {showQR && (
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-xl">
                  <QRCodeSVG 
                    value={profile?.profile_url || window.location.href}
                    size={160}
                    level="H"
                    includeMargin
                  />
                </div>
              </div>
            )}

            {/* Send Money CTA */}
            <Button 
              onClick={handleSendMoney}
              className="w-full h-14 text-lg bg-emerald-500 hover:bg-emerald-600 mb-4"
              data-testid="send-money-cta"
            >
              <Send className="h-5 w-5 mr-2" />
              Send Money
            </Button>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={() => setShowQR(!showQR)}
              >
                <QrCode className="h-4 w-4 mr-2" />
                {showQR ? 'Hide QR' : 'Show QR'}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={copyLink}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={shareProfile}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ParachPay Branding */}
        <div className="text-center">
          <p className="text-emerald-100/50 text-sm">
            Powered by{' '}
            <Link to="/" className="text-emerald-400 hover:underline font-semibold">
              ParachPay
            </Link>
          </p>
          <p className="text-emerald-100/30 text-xs mt-1">
            The global payment gateway for everyone
          </p>
        </div>

        {/* Create Account CTA */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-emerald-100/70 text-sm mb-3">
              Want your own @tag to receive payments?
            </p>
            <Button asChild variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
              <Link to="/register">
                Create Account
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicProfile;
